import { doc, updateDoc, collection, writeBatch, getDoc, increment } from 'firebase/firestore';
import { companiesRef } from './read';
import { db } from '../../config';
import { getDefaultPaymentCycle, getPlanLimits } from '../../../../constants/plans';
import {
  companyPlan,
  masterRecordLimitAutoDefaultNote,
  masterRecordLimitDefault,
  paymentCycle,
} from '../../../../constants/enum';

/**
 * 有料プラン変更時の契約終了日を計算
 * 月末日の溢れを考慮し、翌月の日数を超える場合は翌月末日に丸める
 * 例: 1/31 + 1ヶ月 → 2/28（or 2/29）、3/31 + 1ヶ月 → 4/30
 * @param cycle - 支払いサイクル
 * @param baseDate - 基準日
 * @returns 契約終了日
 */
const getContractEndDate = (cycle: number, baseDate: Date): Date => {
  const endDate = new Date(baseDate);
  if (cycle === paymentCycle.monthly) {
    const targetMonth = endDate.getMonth() + 1;
    const originalDay = endDate.getDate();
    endDate.setMonth(targetMonth, 1);
    const lastDayOfTargetMonth = new Date(
      endDate.getFullYear(),
      endDate.getMonth() + 1,
      0,
    ).getDate();
    endDate.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  } else {
    // yearly またはその他のサイクルはデフォルトで1年後
    endDate.setFullYear(endDate.getFullYear() + 1);
  }
  return endDate;
};

/**
 * 会社情報を更新
 * @param companyId - 更新対象の会社ID
 * @param companyName - 会社名
 * @param phoneNumber - 電話番号
 * @param updatedBy - 更新者のUID
 */
export const updateCompany = async (
  companyId: string,
  companyName: string,
  phoneNumber: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  const updateData = {
    company_name: companyName,
    phone_number: phoneNumber,
    updated_at: new Date(),
    updated_by: updatedBy,
  };

  await updateDoc(companyDoc, updateData);
};

/**
 * 担当者数上限を変更し、履歴を保存
 * @param companyId - 更新対象の会社ID
 * @param beforeLimit - 変更前担当者数上限
 * @param afterLimit - 変更後担当者数上限
 * @param note - 備考
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyUserLimit = async (
  companyId: string,
  beforeLimit: number,
  afterLimit: number,
  note: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  const now = new Date();

  // バッチ処理で担当者数上限の更新と履歴保存を原子的に実行
  const batch = writeBatch(db);

  // 担当者数上限を更新
  batch.update(companyDoc, {
    user_limit: afterLimit,
    updated_at: now,
    updated_by: updatedBy,
  });

  // 担当者数変更履歴をサブコレクションに保存
  const historyRef = collection(companyDoc, 'user_limit_history');
  const historyDoc = doc(historyRef);
  batch.set(historyDoc, {
    before_limit: beforeLimit,
    after_limit: afterLimit,
    note: note,
    created_at: now,
    created_by: updatedBy,
  });

  await batch.commit();
};

export const updateCompanyPatternLimit = async (
  companyId: string,
  beforeLimit: number,
  afterLimit: number,
  note: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  const now = new Date();

  // バッチ処理で読取パターン数上限の更新と履歴保存を原子的に実行
  const batch = writeBatch(db);

  // 読取パターン数上限を更新
  batch.update(companyDoc, {
    pattern_limit: afterLimit,
    updated_at: now,
    updated_by: updatedBy,
  });

  // 読取パターン数変更履歴をサブコレクションに保存
  const historyRef = collection(companyDoc, 'pattern_limit_history');
  const historyDoc = doc(historyRef);
  batch.set(historyDoc, {
    before_limit: beforeLimit,
    after_limit: afterLimit,
    note: note,
    created_at: now,
    created_by: updatedBy,
  });

  await batch.commit();
};

/**
 * AIマスタ照合レコード数上限を変更し、履歴を保存する（#3288）
 * @param companyId - 更新対象の会社ID
 * @param beforeLimit - 変更前の上限値（未設定だった場合は null）
 * @param afterLimit - 変更後の上限値
 * @param note - 備考
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyMasterRecordLimit = async (
  companyId: string,
  beforeLimit: number | null,
  afterLimit: number,
  note: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  const now = new Date();

  // バッチ処理でマスタレコード数上限の更新と履歴保存を原子的に実行
  const batch = writeBatch(db);

  // マスタレコード数上限を更新
  batch.update(companyDoc, {
    master_record_limit: afterLimit,
    updated_at: now,
    updated_by: updatedBy,
  });

  // マスタレコード数上限の変更履歴をサブコレクションに保存
  const historyRef = collection(companyDoc, 'master_record_limit_history');
  const historyDoc = doc(historyRef);
  batch.set(historyDoc, {
    before_limit: beforeLimit,
    after_limit: afterLimit,
    note: note,
    created_at: now,
    created_by: updatedBy,
  });

  await batch.commit();
};

/**
 * 契約プランを変更し、履歴を保存
 * アップグレード時は担当者数上限・OCR月間枠も新プランの上限に更新
 * （ただし、現在の値が新プランの上限を超えている場合はそのまま）
 * ダウングレード時は担当者数上限・OCR月間枠は変更しない
 * @param companyId - 更新対象の会社ID
 * @param beforePlan - 変更前プラン値
 * @param afterPlan - 変更後プラン値
 * @param note - 備考
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyPlan = async (
  companyId: string,
  beforePlan: number,
  afterPlan: number,
  note: string,
  updatedBy: string,
  selectedPaymentCycle?: number,
  selectedPaymentMethod?: number,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  const now = new Date();

  // バッチ処理でプラン更新と履歴保存を原子的に実行
  const batch = writeBatch(db);

  // 支払いサイクルを取得（指定がある場合はそちらを優先）
  const newPaymentCycle = selectedPaymentCycle ?? getDefaultPaymentCycle(afterPlan);

  // 更新データの基本部分
  const updateData: {
    plan: number;
    payment_cycle: number;
    payment_method?: number;
    contract_start_date?: Date;
    contract_end_date?: Date;
    updated_at: Date;
    updated_by: string;
    user_limit?: number;
    ocr_monthly_budget?: number;
    pattern_limit?: number;
  } = {
    plan: afterPlan,
    payment_cycle: newPaymentCycle,
    ...(selectedPaymentMethod !== undefined && { payment_method: selectedPaymentMethod }),
    updated_at: now,
    updated_by: updatedBy,
  };

  // 有料プランへの変更時のみ契約開始日・終了日を設定（フリープランへの変更時は既存の値を維持）
  if (afterPlan !== companyPlan.free) {
    updateData.contract_start_date = now;
    updateData.contract_end_date = getContractEndDate(newPaymentCycle, now);
  }

  // アップグレード時（プランコードが大きくなる場合）は上限値も更新
  if (afterPlan > beforePlan) {
    const companySnapshot = await getDoc(companyDoc);
    const companyData = companySnapshot.data();
    const newPlanLimits = getPlanLimits(afterPlan);

    if (companyData && newPlanLimits) {
      const currentUserLimit = companyData.user_limit ?? 0;
      const currentOcrBudget = companyData.ocr_monthly_budget ?? 0;
      const currentPatternLimit = companyData.pattern_limit ?? 0;

      // 現在の担当者数上限が新プランの上限より小さい場合のみ更新
      if (currentUserLimit < newPlanLimits.userCount) {
        updateData.user_limit = newPlanLimits.userCount;
      }

      // 現在のOCR月間枠が新プランの上限より小さい場合のみ更新
      if (currentOcrBudget < newPlanLimits.ocrMonthlyBudget) {
        updateData.ocr_monthly_budget = newPlanLimits.ocrMonthlyBudget;
      }

      // 現在の読取パターン数上限が新プランの上限より小さい場合のみ更新
      if (currentPatternLimit < newPlanLimits.patternCount) {
        updateData.pattern_limit = newPlanLimits.patternCount;
      }
    }
  }

  // プラン情報を更新
  batch.update(companyDoc, updateData);

  // プラン変更履歴をサブコレクションに保存
  const historyRef = collection(companyDoc, 'plan_change_history');
  const historyDoc = doc(historyRef);
  batch.set(historyDoc, {
    before_plan: beforePlan,
    after_plan: afterPlan,
    note: note,
    created_at: now,
    created_by: updatedBy,
  });

  await batch.commit();
};

/**
 * AI学習許可設定を更新
 * @param companyId - 更新対象の会社ID
 * @param allowAiLearning - AI学習許可フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyAiLearning = async (
  companyId: string,
  allowAiLearning: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  const updateData = {
    allow_ai_learning: allowAiLearning,
    updated_at: new Date(),
    updated_by: updatedBy,
  };

  await updateDoc(companyDoc, updateData);
};

/**
 * マスタ管理機能の有効/無効を更新
 *
 * 有効化する際、レコード数上限が未設定ならデフォルト値を同時に設定する（#3336）。
 * レコード数に応じて月額料金が変わるため、上限なしのまま使える状態にはしない。
 * 既に上限が設定済みの企業（事務局が個別に調整した値）は上書きしない。
 * @param companyId - 更新対象の会社ID
 * @param enableMaster - マスタ管理機能有効フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyEnableMaster = async (
  companyId: string,
  enableMaster: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  const now = new Date();

  const companySnapshot = await getDoc(companyDoc);
  const currentLimit = companySnapshot.data()?.master_record_limit as number | undefined | null;
  // 0 は「制限なし」ではなく設定済みの値として扱うため、null/undefined のみを未設定とみなす
  const needsDefaultLimit = enableMaster && (currentLimit === undefined || currentLimit === null);

  // 上限の設定が不要ならフラグだけを更新する
  if (!needsDefaultLimit) {
    await updateDoc(companyDoc, {
      enable_master: enableMaster,
      updated_at: now,
      updated_by: updatedBy,
    });
    return;
  }

  // フラグ・上限・変更履歴を原子的に書き込む（上限なしで有効化された状態を作らない）
  const batch = writeBatch(db);

  batch.update(companyDoc, {
    enable_master: enableMaster,
    master_record_limit: masterRecordLimitDefault,
    updated_at: now,
    updated_by: updatedBy,
  });

  // 事務局が個別変更したときと同じ履歴に残し、自動設定であることを備考で判別できるようにする
  const historyRef = collection(companyDoc, 'master_record_limit_history');
  batch.set(doc(historyRef), {
    before_limit: null,
    after_limit: masterRecordLimitDefault,
    note: masterRecordLimitAutoDefaultNote,
    created_at: now,
    created_by: updatedBy,
  });

  await batch.commit();
};

/**
 * 企業ユーザー自身によるマスタCSVアップロード可否を更新
 * @param companyId - 更新対象の会社ID
 * @param allowSelfUpload - 自社アップロード許可フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyMasterSelfUpload = async (
  companyId: string,
  allowSelfUpload: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  await updateDoc(companyDoc, {
    master_self_upload: allowSelfUpload,
    updated_at: new Date(),
    updated_by: updatedBy,
  });
};

/**
 * 辞書変換機能の有効/無効を更新
 * @param companyId - 更新対象の会社ID
 * @param enableDictionary - 辞書変換機能有効フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyEnableDictionary = async (
  companyId: string,
  enableDictionary: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  await updateDoc(companyDoc, {
    enable_dictionary: enableDictionary,
    updated_at: new Date(),
    updated_by: updatedBy,
  });
};

/**
 * 指定フォーマット出力の有効/無効を更新（#3070）
 * @param companyId - 更新対象の会社ID
 * @param enableExportTemplate - 指定フォーマット出力の有効フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyEnableExportTemplate = async (
  companyId: string,
  enableExportTemplate: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  await updateDoc(companyDoc, {
    enable_export_template: enableExportTemplate,
    updated_at: new Date(),
    updated_by: updatedBy,
  });
};

/**
 * 企業別カスタム関数の有効/無効を更新（#3014/#3074）
 * @param companyId - 更新対象の会社ID
 * @param enableCustomFunctions - カスタム関数の有効フラグ
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyEnableCustomFunctions = async (
  companyId: string,
  enableCustomFunctions: boolean,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  await updateDoc(companyDoc, {
    enable_custom_functions: enableCustomFunctions,
    updated_at: new Date(),
    updated_by: updatedBy,
  });
};

/**
 * CRM IDを更新
 * @param companyId - 更新対象の会社ID
 * @param crmId - CRM ID（空文字でクリア）
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyCrmId = async (
  companyId: string,
  crmId: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  const updateData = {
    crm_id: crmId,
    updated_at: new Date(),
    updated_by: updatedBy,
  };

  await updateDoc(companyDoc, updateData);
};

/**
 * Stripe設定（Customer ID・Subscription ID）を一括更新
 * @param companyId - 更新対象の会社ID
 * @param stripeCustomerId - Stripe Customer ID（空文字でクリア）
 * @param stripeSubscriptionId - Stripe Subscription ID（空文字でクリア）
 * @param updatedBy - 更新者のUID
 */
export const updateCompanyStripeIds = async (
  companyId: string,
  stripeCustomerId: string,
  stripeSubscriptionId: string,
  updatedBy: string,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);

  await updateDoc(companyDoc, {
    stripe_customer_id: stripeCustomerId,
    stripe_subscription_id: stripeSubscriptionId,
    updated_at: new Date(),
    updated_by: updatedBy,
  });
};

/**
 * 読取パターン数を増減する
 * @param companyId - 対象の会社ID
 * @param delta - 増減値（+1: 作成時, -1: 削除時）
 */
export const updateCompanyPatternCount = async (
  companyId: string,
  delta: number,
): Promise<void> => {
  const companyDoc = doc(companiesRef, companyId);
  await updateDoc(companyDoc, {
    current_pattern_count: increment(delta),
  });
};
